import type {
  ComputeCaseDto,
  ComputeCaseInputSettingsDto,
  ListComputeSettingsModelDto,
  ModelAreaDto,
} from '../../../api/generated';

const wholeModelAreaKey = 'whole model';
const emptyModelAreaId = '00000000-0000-0000-0000-000000000000';

const normalizeArea = (modelArea: ModelAreaDto | undefined) => {
  const areaId = modelArea?.modelAreaId?.trim();
  const areaType = modelArea?.modelAreaType?.trim().toLowerCase();

  if (
    areaType === wholeModelAreaKey ||
    areaId === emptyModelAreaId ||
    (!areaId && !areaType)
  ) {
    return wholeModelAreaKey;
  }

  return areaId || areaType || wholeModelAreaKey;
};

const normalizeSavedArea = (modelArea: ComputeCaseDto['modelArea']) => {
  const areaId = modelArea?.modelAreaId?.trim();
  const areaName = modelArea?.name?.trim().toLowerCase();

  if (
    areaName === wholeModelAreaKey ||
    areaId === emptyModelAreaId ||
    (!areaId && !areaName)
  ) {
    return wholeModelAreaKey;
  }

  return areaId || areaName || wholeModelAreaKey;
};

const settingKey = (
  setting: ListComputeSettingsModelDto | ComputeCaseInputSettingsDto,
) =>
  [setting.computeMethod, setting.inputValueType, setting.value, setting.name]
    .map((value) => value?.trim().toLowerCase() ?? '')
    .join('|');

const settingNameKey = (
  setting: ListComputeSettingsModelDto | ComputeCaseInputSettingsDto,
) =>
  [setting.computeMethod, setting.inputValueType, setting.name]
    .map((value) => value?.trim().toLowerCase() ?? '')
    .join('|');

const settingsMatch = (
  firstSetting: ComputeCaseInputSettingsDto | ListComputeSettingsModelDto,
  secondSetting: ComputeCaseInputSettingsDto | ListComputeSettingsModelDto,
) => {
  if (
    firstSetting.computeSettingId &&
    firstSetting.computeSettingId === secondSetting.computeSettingId
  )
    return true;

  if (settingKey(firstSetting) === settingKey(secondSetting)) return true;

  const firstValue = firstSetting.value?.trim().toLowerCase();
  const secondValue = secondSetting.value?.trim().toLowerCase();
  if (
    firstSetting.inputValueType === 'VariogramFamilyFilter' &&
    secondSetting.inputValueType === 'VariogramFamilyFilter' &&
    firstValue &&
    secondValue &&
    firstValue !== secondValue
  )
    return false;

  return settingNameKey(firstSetting) === settingNameKey(secondSetting);
};

export const getVariogramMethodOptions = (
  settings: ListComputeSettingsModelDto[] | null | undefined,
  computeMethod: ComputeCaseDto['computeMethod'],
) => {
  const methodSettings = settings?.filter(
    (setting) => setting.computeMethod === computeMethod,
  );
  const primaryInputValueType =
    computeMethod === 'Indicator'
      ? 'Indicator'
      : computeMethod === 'NetToGross'
      ? 'NetToGross'
      : 'AttributeName';

  return {
    parameterOptions:
      methodSettings?.filter(
        (setting) =>
          setting.inputValueType === primaryInputValueType &&
          setting.value !== '0',
      ) ?? [],
    modelOptions:
      methodSettings?.filter(
        (setting) => setting.inputValueType === 'VariogramFamilyFilter',
      ) ?? [],
    additionalParameterOptions:
      computeMethod === 'Indicator'
        ? []
        : methodSettings?.filter(
            (setting) =>
              setting.inputValueType === 'Archel' && setting.value !== '0',
          ) ?? [],
  };
};

const hasSettingsInCase = (
  existingCase: ComputeCaseDto,
  selectedModelArea: ModelAreaDto,
  requiredSettings: ListComputeSettingsModelDto[],
) =>
  normalizeSavedArea(existingCase.modelArea) ===
    normalizeArea(selectedModelArea) &&
  requiredSettings.every((requiredSetting) =>
    existingCase.inputSettings.some((existingSetting) =>
      settingsMatch(existingSetting, requiredSetting),
    ),
  );

const hasSameSettings = (
  existingSettings: ComputeCaseInputSettingsDto[],
  selectedSettings: ListComputeSettingsModelDto[],
) =>
  existingSettings.length === selectedSettings.length &&
  selectedSettings.every((selectedSetting) =>
    existingSettings.some((existingSetting) =>
      settingsMatch(existingSetting, selectedSetting),
    ),
  );

const hasSetBasedCombinationInCase = (
  existingCase: ComputeCaseDto,
  modelArea: ModelAreaDto,
  primarySetting: ListComputeSettingsModelDto,
  archel: ListComputeSettingsModelDto | undefined,
  model: ListComputeSettingsModelDto,
) =>
  hasSettingsInCase(existingCase, modelArea, [primarySetting, model]) &&
  (archel
    ? existingCase.inputSettings.some((existingSetting) =>
        settingsMatch(existingSetting, archel),
      )
    : existingCase.inputSettings.every(
        (existingSetting) => existingSetting.inputValueType !== 'Archel',
      ));

const hasSetBasedVariogramCollision = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  computeMethod: ComputeCaseDto['computeMethod'],
  modelArea: ModelAreaDto,
  selectedSettings: ListComputeSettingsModelDto[],
) => {
  const primarySettings = selectedSettings.filter(
    (setting) =>
      setting.inputValueType !== 'Archel' &&
      setting.inputValueType !== 'VariogramFamilyFilter',
  );
  const archels = selectedSettings.filter(
    (setting) => setting.inputValueType === 'Archel',
  );
  const models = selectedSettings.filter(
    (setting) => setting.inputValueType === 'VariogramFamilyFilter',
  );

  const archelCoordinates = archels.length > 0 ? archels : [undefined];

  return primarySettings.some((primarySetting) =>
    archelCoordinates.some((archel) =>
      models.some((model) =>
        cases.some(
          (existingCase) =>
            existingCase.computeCaseId !== currentCaseId &&
            existingCase.computeMethod === computeMethod &&
            hasSetBasedCombinationInCase(
              existingCase,
              modelArea,
              primarySetting,
              archel,
              model,
            ),
        ),
      ),
    ),
  );
};

const hasIndicatorVariogramCollision = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  modelArea: ModelAreaDto,
  selectedSettings: ListComputeSettingsModelDto[],
) => {
  const parameters = selectedSettings.filter(
    (setting) => setting.inputValueType === 'Indicator',
  );
  const models = selectedSettings.filter(
    (setting) => setting.inputValueType === 'VariogramFamilyFilter',
  );

  return parameters.some((parameter) =>
    models.some((model) =>
      cases.some(
        (existingCase) =>
          existingCase.computeCaseId !== currentCaseId &&
          existingCase.computeMethod === 'Indicator' &&
          hasSettingsInCase(existingCase, modelArea, [parameter, model]),
      ),
    ),
  );
};

export const hasDuplicateVariogramCase = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  computeMethod: ComputeCaseDto['computeMethod'],
  selectedModelArea: ModelAreaDto[] | undefined,
  selectedParameters: ListComputeSettingsModelDto[] | undefined,
) => {
  const modelArea = selectedModelArea?.[0];
  if (!modelArea || !selectedParameters?.length) return false;

  const hasAtomicCollision =
    computeMethod === 'Indicator'
      ? hasIndicatorVariogramCollision(
          cases,
          currentCaseId,
          modelArea,
          selectedParameters,
        )
      : computeMethod === 'NetToGross' ||
        computeMethod === 'ContiniousParameter'
      ? hasSetBasedVariogramCollision(
          cases,
          currentCaseId,
          computeMethod,
          modelArea,
          selectedParameters,
        )
      : false;

  return (
    cases.some(
      (existingCase) =>
        existingCase.computeCaseId !== currentCaseId &&
        existingCase.computeMethod === computeMethod &&
        normalizeSavedArea(existingCase.modelArea) ===
          normalizeArea(modelArea) &&
        hasSameSettings(existingCase.inputSettings, selectedParameters),
    ) || hasAtomicCollision
  );
};

export const isIndicatorOptionExhausted = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  selectedModelArea: ModelAreaDto[] | undefined,
  selectedParameters: ListComputeSettingsModelDto[],
  selectedModels: ListComputeSettingsModelDto[],
  option: ListComputeSettingsModelDto,
  availableParameters: ListComputeSettingsModelDto[],
  availableModels: ListComputeSettingsModelDto[],
) => {
  const modelArea = selectedModelArea?.[0];
  if (!modelArea) return false;

  const hasCell = (
    parameter: ListComputeSettingsModelDto,
    model: ListComputeSettingsModelDto,
  ) =>
    cases.some(
      (existingCase) =>
        existingCase.computeCaseId !== currentCaseId &&
        existingCase.computeMethod === 'Indicator' &&
        hasSettingsInCase(existingCase, modelArea, [parameter, model]),
    );

  const optionIsModel = option.inputValueType === 'VariogramFamilyFilter';
  if (optionIsModel) {
    if (selectedParameters.length > 0)
      return selectedParameters.some((parameter) => hasCell(parameter, option));

    return (
      availableParameters.length > 0 &&
      availableParameters.every((parameter) => hasCell(parameter, option))
    );
  }

  return (
    availableModels.length > 0 &&
    availableModels.every((model) => hasCell(option, model))
  );
};

export const isSetBasedVariogramOptionExhausted = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  computeMethod: ComputeCaseDto['computeMethod'],
  selectedModelArea: ModelAreaDto[] | undefined,
  selectedPrimarySettings: ListComputeSettingsModelDto[],
  selectedArchels: ListComputeSettingsModelDto[],
  selectedModels: ListComputeSettingsModelDto[],
  option: ListComputeSettingsModelDto,
  availableModels: ListComputeSettingsModelDto[] = [],
  availableArchels: ListComputeSettingsModelDto[] = [],
) => {
  const modelArea = selectedModelArea?.[0];
  const primarySettings =
    option.inputValueType === 'Archel' ||
    option.inputValueType === 'VariogramFamilyFilter'
      ? selectedPrimarySettings
      : [option];
  if (!modelArea || primarySettings.length === 0) return false;

  const hasCombination = (
    primarySetting: ListComputeSettingsModelDto,
    archel: ListComputeSettingsModelDto | undefined,
    model: ListComputeSettingsModelDto,
  ) =>
    cases.some(
      (existingCase) =>
        existingCase.computeCaseId !== currentCaseId &&
        existingCase.computeMethod === computeMethod &&
        normalizeSavedArea(existingCase.modelArea) ===
          normalizeArea(modelArea) &&
        hasSetBasedCombinationInCase(
          existingCase,
          modelArea,
          primarySetting,
          archel,
          model,
        ),
    );

  if (option.inputValueType === 'Archel') {
    const modelsToCheck =
      selectedModels.length > 0 ? selectedModels : availableModels;
    if (modelsToCheck.length === 0) return false;

    if (selectedModels.length === 0)
      return primarySettings.every((primarySetting) =>
        modelsToCheck.every((model) =>
          hasCombination(primarySetting, option, model),
        ),
      );

    return primarySettings.some((primarySetting) =>
      modelsToCheck.some((model) =>
        hasCombination(primarySetting, option, model),
      ),
    );
  }

  if (option.inputValueType === 'VariogramFamilyFilter') {
    const archelCoordinates =
      selectedArchels.length > 0 ? selectedArchels : [undefined];
    return primarySettings.some((primarySetting) =>
      archelCoordinates.some((archel) =>
        hasCombination(primarySetting, archel, option),
      ),
    );
  }

  const modelsToCheck =
    selectedModels.length > 0 ? selectedModels : availableModels;
  if (modelsToCheck.length === 0) return false;

  const archelsToCheck =
    selectedArchels.length > 0
      ? selectedArchels
      : selectedModels.length > 0
      ? [undefined]
      : [undefined, ...availableArchels];

  if (selectedModels.length === 0) {
    return archelsToCheck.every((archel) =>
      modelsToCheck.every((model) => hasCombination(option, archel, model)),
    );
  }

  return archelsToCheck.some((archel) =>
    modelsToCheck.some((model) => hasCombination(option, archel, model)),
  );
};

export const isNetToGrossOptionExhausted = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  selectedModelArea: ModelAreaDto[] | undefined,
  selectedGrain: ListComputeSettingsModelDto | undefined,
  selectedArchels: ListComputeSettingsModelDto[],
  selectedModels: ListComputeSettingsModelDto[],
  option: ListComputeSettingsModelDto,
  availableModels: ListComputeSettingsModelDto[] = [],
  availableArchels: ListComputeSettingsModelDto[] = [],
) =>
  isSetBasedVariogramOptionExhausted(
    cases,
    currentCaseId,
    'NetToGross',
    selectedModelArea,
    selectedGrain ? [selectedGrain] : [],
    selectedArchels,
    selectedModels,
    option,
    availableModels,
    availableArchels,
  );

export const isVariogramModelAreaExhausted = (
  cases: ComputeCaseDto[],
  currentCaseId: string,
  computeMethod: ComputeCaseDto['computeMethod'],
  modelArea: ModelAreaDto,
  parameterOptions: ListComputeSettingsModelDto[],
  modelOptions: ListComputeSettingsModelDto[],
  additionalOptions: ListComputeSettingsModelDto[] = [],
) => {
  if (parameterOptions.length === 0 || modelOptions.length === 0) return false;

  if (
    computeMethod === 'NetToGross' ||
    computeMethod === 'ContiniousParameter'
  ) {
    const archelCoordinates = [undefined, ...additionalOptions];

    return parameterOptions.every((parameter) =>
      modelOptions.every((model) =>
        archelCoordinates.every((archel) =>
          cases.some(
            (existingCase) =>
              existingCase.computeCaseId !== currentCaseId &&
              existingCase.computeMethod === computeMethod &&
              hasSetBasedCombinationInCase(
                existingCase,
                modelArea,
                parameter,
                archel,
                model,
              ),
          ),
        ),
      ),
    );
  }

  const dimensions = [parameterOptions, modelOptions, additionalOptions].filter(
    (dimension) => dimension.length > 0,
  );

  const everyCombinationExists = (
    dimensionIndex: number,
    settings: ListComputeSettingsModelDto[],
  ): boolean => {
    if (dimensionIndex === dimensions.length) {
      return cases.some(
        (existingCase) =>
          existingCase.computeCaseId !== currentCaseId &&
          existingCase.computeMethod === computeMethod &&
          hasSettingsInCase(existingCase, modelArea, settings),
      );
    }

    return dimensions[dimensionIndex].every((dimensionOption) =>
      everyCombinationExists(dimensionIndex + 1, [
        ...settings,
        dimensionOption,
      ]),
    );
  };

  return everyCombinationExists(0, []);
};

export const isVariogramTypeExhausted = (
  cases: ComputeCaseDto[],
  computeMethod: ComputeCaseDto['computeMethod'],
  modelAreas: ModelAreaDto[],
  parameterOptions: ListComputeSettingsModelDto[],
  modelOptions: ListComputeSettingsModelDto[],
  additionalOptions: ListComputeSettingsModelDto[] = [],
) =>
  modelAreas.length > 0 &&
  modelAreas.every((modelArea) =>
    isVariogramModelAreaExhausted(
      cases,
      '',
      computeMethod,
      modelArea,
      parameterOptions,
      modelOptions,
      additionalOptions,
    ),
  );
